import { dateLocale } from "@/lib/dateTime";
/** Shared helpers for list-page filters (owner, date ranges, option lists) */

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

const DAY = 86400000;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export const PAST_DATE_PRESETS: FilterOption[] = [
  { value: "any", label: "Any time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This week" },
  { value: "last_7", label: "Last 7 days" },
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "last_30", label: "Last 30 days" },
  { value: "this_quarter", label: "This quarter" },
  { value: "custom", label: "Custom range…" },
];

/** For due / close dates, which mostly lie ahead */
export const FUTURE_DATE_PRESETS: FilterOption[] = [
  { value: "any", label: "Any time" },
  { value: "overdue", label: "Overdue" },
  { value: "today", label: "Today" },
  { value: "this_week", label: "This week" },
  { value: "next_7", label: "Next 7 days" },
  { value: "this_month", label: "This month" },
  { value: "next_30", label: "Next 30 days" },
  { value: "this_quarter", label: "This quarter" },
  { value: "no_date", label: "No date set" },
  { value: "custom", label: "Custom range…" },
];

/** [from, to) for a preset, in local time. "custom:YYYY-MM-DD:YYYY-MM-DD" holds a custom range. */
export function dateRangeBounds(filter: string): [Date | null, Date | null] | null {
  if (!filter || filter === "any" || filter === "custom" || filter === "custom:" || filter === "custom::") {
    return null;
  }

  const today = startOfDay(new Date());
  const tomorrow = new Date(today.getTime() + DAY);
  // Week starts on Monday
  const weekStart = new Date(today.getTime() - ((today.getDay() + 6) % 7) * DAY);
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);

  switch (filter) {
    case "today":
      return [today, tomorrow];
    case "yesterday":
      return [new Date(today.getTime() - DAY), today];
    case "this_week":
      return [weekStart, new Date(weekStart.getTime() + 7 * DAY)];
    case "last_7":
      return [new Date(today.getTime() - 6 * DAY), tomorrow];
    case "next_7":
      return [today, new Date(today.getTime() + 7 * DAY)];
    case "this_month":
      return [monthStart, new Date(today.getFullYear(), today.getMonth() + 1, 1)];
    case "last_month":
      return [new Date(today.getFullYear(), today.getMonth() - 1, 1), monthStart];
    case "last_30":
      return [new Date(today.getTime() - 29 * DAY), tomorrow];
    case "next_30":
      return [today, new Date(today.getTime() + 30 * DAY)];
    case "this_quarter":
      return [quarterStart, new Date(quarterStart.getFullYear(), quarterStart.getMonth() + 3, 1)];
    case "overdue":
      return [null, today];
  }

  if (filter.startsWith("custom")) {
    const parts = filter.split(":");
    const fromStr = parts[1]?.trim();
    const toStr = parts[2]?.trim();
    if (!fromStr && !toStr) return null;

    let fromDate: Date | null = null;
    if (fromStr) {
      const partsArr = fromStr.split("-").map(Number);
      const y = partsArr[0];
      const m = partsArr[1];
      const d = partsArr[2];
      if (y !== undefined && m !== undefined && d !== undefined && !isNaN(y) && !isNaN(m) && !isNaN(d)) {
        fromDate = new Date(y, m - 1, d, 0, 0, 0, 0);
      }
    }

    let toDate: Date | null = null;
    if (toStr) {
      const partsArr = toStr.split("-").map(Number);
      const y = partsArr[0];
      const m = partsArr[1];
      const d = partsArr[2];
      if (y !== undefined && m !== undefined && d !== undefined && !isNaN(y) && !isNaN(m) && !isNaN(d)) {
        // Upper bound: start of next day (so entire 'to' date is included in query)
        toDate = new Date(y, m - 1, d + 1, 0, 0, 0, 0);
      }
    }

    if (!fromDate && !toDate) return null;
    return [fromDate, toDate];
  }

  return null;
}

export function dateFilterLabel(filter: string, presets: FilterOption[] = PAST_DATE_PRESETS): string {
  if (!filter || filter === "any") return "Any time";
  if (filter.startsWith("custom")) {
    const parts = filter.split(":");
    const from = parts[1]?.trim();
    const to = parts[2]?.trim();
    if (!from && !to) return "Custom range";

    const fmt = (s?: string) => {
      if (!s) return "";
      try {
        const partsArr = s.split("-").map(Number);
        const y = partsArr[0];
        const m = partsArr[1];
        const d = partsArr[2];
        if (y === undefined || m === undefined || d === undefined || isNaN(y) || isNaN(m) || isNaN(d)) return s;
        const dt = new Date(y, m - 1, d);
        return dt.toLocaleDateString(dateLocale(), { day: "2-digit", month: "short" });
      } catch {
        return s;
      }
    };

    if (from && to) return `${fmt(from)} – ${fmt(to)}`;
    if (from) return `From ${fmt(from)}`;
    if (to) return `Up to ${fmt(to)}`;
    return "Custom range";
  }
  return [...presets, ...FUTURE_DATE_PRESETS].find((p) => p.value === filter)?.label || filter;
}

/** Turns a date filter ("this_month", "custom:2026-10-01:2026-10-31"…) into API params `<prefix>_from` / `<prefix>_to` */
export function dateRangeParams(filter: string, prefix: string): Record<string, string> {
  const bounds = filter && filter !== "any" ? dateRangeBounds(filter) : null;
  if (!bounds) return {};
  const [from, to] = bounds;
  return {
    ...(from ? { [`${prefix}_from`]: from.toISOString() } : {}),
    ...(to ? { [`${prefix}_to`]: to.toISOString() } : {}),
  };
}

/** Checks whether a filter field has an active, applied value */
export function isFilterActiveValue(defaultValue: string, value: string | undefined, type?: "select" | "date"): boolean {
  const v = value ?? defaultValue;
  if (v === defaultValue) return false;
  if (type === "date" && (v === "custom" || v === "custom:" || v === "custom::")) return false;
  return true;
}

/** Builds a query string, dropping empty values and "all" / "any" (meaning no filter) */
export function toQuery(params: Record<string, string | number | undefined | null>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "" && v !== "all" && v !== "any") qs.set(k, String(v));
  }
  const str = qs.toString();
  return str ? `?${str}` : "";
}

export interface OwnerFacet {
  id: string | null;
  name: string;
  count: number;
}

/** Owner choices from the API's FilterOptions (counts are within the user's scope) */
export function ownerOptionsFromFacets(facets: OwnerFacet[] = [], total = 0, currentUserId?: string | number | null): FilterOption[] {
  const myId = currentUserId != null ? String(currentUserId) : null;
  const mine = facets.find((f) => f.id && f.id === myId)?.count || 0;
  const unassigned = facets.find((f) => !f.id)?.count || 0;
  return [
    { value: "all", label: "All owners", count: total },
    { value: "me", label: "Me", count: mine },
    { value: "unassigned", label: "Unassigned", count: unassigned },
    ...facets
      .filter((f) => f.id && f.id !== myId)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((f) => ({ value: f.id!, label: f.name, count: f.count })),
  ];
}

/** Value choices (source, industry, state…) from the API's FilterOptions */
export function valueOptionsFromFacets(facets: { value: string; count: number }[] = [], allLabel: string, total = 0, pretty = false): FilterOption[] {
  const label = (v: string) => (pretty ? v.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : v);
  return [{ value: "all", label: allLabel, count: total }, ...facets.map((f) => ({ value: f.value, label: label(f.value), count: f.count }))];
}
